import { addDays, isAfter, startOfDay } from 'date-fns';

import { geefInzetOmschrijvingVoorElement } from '@/domain/conflict/conflictUtils';
import { Conflict, ConflictElement } from '@/types/Conflicten';
import { timeWithDayOfWeek } from '@/utils/dateUtils';

export {
  geefInzetOmschrijvingVoorElement,
  geefVerkeersdagTijdstipVoorElement,
  isVerkeersdagVandaag,
} from '@/domain/conflict/conflictUtils';

export const vindEersteInzet = (a: Conflict) =>
  a.elementen.find(
    (el) => (el.claimSoort === undefined || el.claimSoort === null || el.claimSoort === 'EBKSB') && !el.isGeaccepteerd,
  );

// VEKEERD_MAT en VERSTERKT conflicten, om eerste inzet te vinden (kan ook claims zijn)
export function vindEersteNietGeaccepteerdInzet(conflict: Conflict) {
  return conflict.elementen.find((el) => !el.isGeaccepteerd);
}

/** Sorts 2 conflicts gebaseer op:
 * 'Hardheid' van verkeerd mat
 * Type, versterkt of verkeerd
 * Uitvoeringstijd
 * @param a
 * @param b
 * @returns
 */
export const ddpConflictVerkeerdMatComparator = (a: Conflict, b: Conflict) => {
  /** Eerste: Sorteer 2 conflicten gebaseer op 'hardheid' van conflict, e.g.:
   *  Een 'zacht' conflict het een geplande type van 'VIRM-6' en materieel type van 'VIRM-4'
   *  Een 'harde' conflict het een geplande type van 'VIRM-6' en materieel type van 'SLT-3' of een geplande type 'SNG-3' en materieel type van 'SNG-3T'
   */
  const aIsHard = isHardVerkeerdMat(a);
  const bIsHard = isHardVerkeerdMat(b);

  if (aIsHard && !bIsHard) return -1;
  if (!aIsHard && bIsHard) return 1;

  // Tweede: sorteer volgens type
  const typeOrder = { VERSTERKT_MATERIEEL: 0, VERKEERD_MATERIEEL: 1 };
  const typeComparison = typeOrder[a.type] - typeOrder[b.type];
  if (typeComparison !== 0) return typeComparison;

  // Derde: sorteer volgens uitvoeringstijd (sla geaccepteerde elementen over)
  const aTime = geefTijdstipVoorElement(vindEersteNietGeaccepteerdInzet(a))?.getTime() ?? 0;
  const bTime = geefTijdstipVoorElement(vindEersteNietGeaccepteerdInzet(b))?.getTime() ?? 0;
  if (aTime > bTime) return 1;
  if (bTime > aTime) return -1;
  return 0;
};

/** Sorts conflicts where all elements are accepted to the bottom.
 * Conflicts with remaining (non-accepted) elements are sorted before fully accepted ones.
 */
export const ddpConflictAcceptedLastComparator = (a: Conflict, b: Conflict) => {
  const aAllAccepted = a.elementen.every((el) => el.isGeaccepteerd);
  const bAllAccepted = b.elementen.every((el) => el.isGeaccepteerd);
  if (aAllAccepted === bAllAccepted) return 0;
  return aAllAccepted ? 1 : -1;
};

/** Compare the times of a conflict and sort them ASC.
 *
 * @param a
 * @param b
 * @returns
 */
export const ddpConflictTimeComparator = (a: Conflict, b: Conflict) => {
  const [ia, ib] = [geefTijdstipVoorConflict(a)?.getTime() ?? 0, geefTijdstipVoorConflict(b)?.getTime() ?? 0];
  if (ia > ib) return 1;
  if (ib > ia) return -1;
  return 0;
};

const M_DASH = '\u2014';

export const geefStelOfOorspronkelijkDienstVoorConflict = (conflict: Conflict) => {
  return (
    conflict.eenheidNummer ??
    conflict.elementen[0]?.oorspronkelijkeDienst ??
    conflict.elementen[1]?.oorspronkelijkeDienst
  );
};

export const geefStelOfOorspronkelijkDienstVoorMancoConflict = (conflictElement: ConflictElement | undefined) => {
  if (
    conflictElement?.oorspronkelijkeDienst &&
    isAfter(new Date(conflictElement.uitvoeringstijd), new Date(startOfDay(addDays(new Date(), 1))))
  ) {
    return `${timeWithDayOfWeek(new Date(conflictElement.uitvoeringstijd))}: ${conflictElement?.oorspronkelijkeDienst}`;
  }
  return conflictElement?.oorspronkelijkeDienst;
};

export const geefInzetOmschrijvingVoorConflict = (element: ConflictElement | undefined) => {
  return (element && geefInzetOmschrijvingVoorElement(element)) ?? M_DASH;
};

export const geefInzetVoorConflict = (conflict: Conflict) => {
  return vindEersteInzet(conflict);
};

export const geefTijdstipVoorElement = (element: ConflictElement | undefined | null) =>
  element?.uitvoeringstijd ? new Date(element?.uitvoeringstijd) : undefined;

export const geefTijdstipVoorConflict = (conflict: Conflict) => geefTijdstipVoorElement(vindEersteInzet(conflict));

export const geefDienstregelpuntVoorElement = (element: ConflictElement | undefined) =>
  element?.dienstregelpunt ?? M_DASH;

/** Of ergens in het conflict (huidige of latere inzet in dezelfde werklijn) een bijzonderheidscode voorkomt.
 * Gebruikt om conflicten met een bijzonderheidscode bovenaan te sorteren.
 */
export const conflictHeeftBijzonderheidscode = (conflict: Conflict): boolean =>
  conflict.elementen.some((element) => !!element.bijzonderheidscode);

/** Geeft de unieke bijzonderheidscodes van inzetten die ná `huidigElement` komen in dezelfde werklijn.
 * Een inzet bestaat uit meerdere elementen (bv. vertrek- en aankomstelement) met hetzelfde `werklijnElementId`,
 * dus die worden hier niet dubbel geteld.
 */
export const geefOpvolgendeBijzonderheidscodesVoorConflict = (
  conflict: Conflict,
  huidigElement: ConflictElement | undefined,
): string[] => {
  const huidigeIndex = huidigElement ? conflict.elementen.indexOf(huidigElement) : -1;
  const opvolgendeElementen =
    huidigeIndex >= 0 ? conflict.elementen.slice(huidigeIndex + 1) : conflict.elementen.slice(1);

  const geefSleutel = (element: ConflictElement) =>
    element.werklijnElementId ?? `${element.treinNummer}-${element.uitvoeringstijd}`;
  const huidigSleutel = huidigElement ? geefSleutel(huidigElement) : undefined;

  const codes: string[] = [];
  opvolgendeElementen.forEach((element) => {
    if (geefSleutel(element) === huidigSleutel) return;
    if (element.bijzonderheidscode && !codes.includes(element.bijzonderheidscode)) {
      codes.push(element.bijzonderheidscode);
    }
  });
  return codes;
};

/** Sorteert conflicten met een bijzonderheidscode (huidig of later in de werklijn) bovenaan.
 * Binnen deze groepen geldt geen eigen rangorde; de standaardsortering (tijd/hardheid) blijft gelden.
 */
export const ddpConflictBijzonderheidscodeComparator = (a: Conflict, b: Conflict) => {
  const aHeeft = conflictHeeftBijzonderheidscode(a);
  const bHeeft = conflictHeeftBijzonderheidscode(b);
  if (aHeeft && !bHeeft) return -1;
  if (!aHeeft && bHeeft) return 1;
  return 0;
};

/** Map conflicten to eenheid.
 *
 * @param conflicten
 * @returns
 */
export const getConflictenByStel = (conflicten: Conflict[]): { [stel: string]: Conflict } =>
  conflicten.reduce((obj, conflict) => {
    if (conflict.eenheidNummer !== null) obj[conflict.eenheidNummer] = conflict;
    return obj;
  }, {});

/** Geef het matType voor een conflict.
 *
 * @param conflict
 * @returns
 */
export const geefConflictMaterieelSoort = (conflict: Conflict): string => {
  let mat = `${conflict.eenheidSoort}-${conflict.eenheidAanduiding}`;
  if (mat.includes('null')) {
    if (conflict.type === 'MANCO')
      mat = `${conflict.elementen[0].materieelSoort}-${conflict.elementen[0].materieelAanduiding}`;
    if (conflict.type === 'PLAATS')
      mat = `${conflict.elementen.at(-1)?.materieelSoort}-${conflict.elementen.at(-1)?.materieelAanduiding}`;
  }
  return mat;
};

/** Het veschil tussen een hard en een zacht conflict bestaat wanneer het gereden materieel niet van hetzelfde type is (met een andere aanduiding).
 *
 * @param conflict
 */
export const isHardVerkeerdMat = (conflict: Conflict): boolean => {
  const regex = /\d*/gm;
  const geredenMat = `${conflict.eenheidSoort}-${conflict.eenheidAanduiding}`.replace(regex, '');
  const plannendMat =
    `${conflict.elementen.at(-1)?.materieelSoort}-${conflict.elementen.at(-1)?.materieelAanduiding}`.replace(regex, '');
  return geredenMat !== plannendMat;
};
